
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/auth';
import { getPatients, addVital, addLog, getPatientVitals, addEditRequest } from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { Patient, VitalReading } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

export default function TemperatureEntry() {
  const navigate = useNavigate();
  const user = getUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [temperature, setTemperature] = useState('');
  const [unit, setUnit] = useState<'celsius' | 'fahrenheit'>('celsius');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alreadyRecorded, setAlreadyRecorded] = useState(false);
  const [existingVital, setExistingVital] = useState<VitalReading | null>(null);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newTemp, setNewTemp] = useState('');
  const [newTempUnit, setNewTempUnit] = useState<'celsius' | 'fahrenheit'>('celsius');
  const [updateReason, setUpdateReason] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    if (!user || !['temperature', 'admin', 'master_admin', 'all_vitals'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    setSelectedPatient(null);
    setAlreadyRecorded(false);
    setExistingVital(null);
    if (value.trim().length > 0) {
      const patients = await getPatients();
      const results = patients.filter(
        (p) =>
          p.id.toLowerCase().includes(value.toLowerCase()) ||
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          p.contactNumber.includes(value)
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const selectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.name} (${patient.id})`);
    setShowResults(false);
    const patientVitals = await getPatientVitals(patient.id);
    const tempVital = patientVitals.find(v => v.type === 'temperature');
    if (tempVital) {
      setAlreadyRecorded(true);
      setExistingVital(tempVital);
    } else {
      setAlreadyRecorded(false);
      setExistingVital(null);
    }
    setError('');
  };

  const convertToCelsius = (value: string, fromUnit: string): string => {
    if (fromUnit === 'fahrenheit') {
      return ((parseFloat(value) - 32) * 5 / 9).toFixed(2);
    }
    return value;
  };

  const getPreviewValue = (): string => {
    if (!temperature) return '--';
    const tempValue = parseFloat(temperature);
    if (isNaN(tempValue)) return '--';
    if (unit === 'celsius') return `${tempValue.toFixed(1)}°C`;
    const celsius = (tempValue - 32) * 5 / 9;
    return `${tempValue.toFixed(1)}°F (${celsius.toFixed(1)}°C)`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedPatient || !user) { setError('Please select a patient first.'); return; }
    if (alreadyRecorded) { setError('Temperature already recorded. Use "Request Update" to change it.'); return; }
    if (!temperature) { setError('Please enter a temperature value.'); return; }

    setLoading(true);
    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();
    const tempInCelsius = convertToCelsius(temperature, unit);

    addVital({
      id: `V${Date.now()}_TEMP`,
      patientId: selectedPatient.id,
      type: 'temperature',
      value: tempInCelsius,
      unit: '°C',
      recordedAt: timestamp,
      recordedBy: user.username,
    });

    addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'Temperature Entry',
      details: `Recorded temperature ${tempInCelsius}°C for patient ${selectedPatient.name} (${selectedPatient.id})`,
      ipAddress, deviceName: device, browser, timestamp,
    });

    setSuccess(true);
    setLoading(false);
    setTimeout(() => {
      setTemperature(''); setUnit('celsius'); setSelectedPatient(null);
      setSearchTerm(''); setSuccess(false); setAlreadyRecorded(false); setExistingVital(null);
    }, 2000);
  };

  const handleUpdateRequest = async () => {
    if (!selectedPatient || !existingVital || !user) return;
    if (!newTemp) { setError('Please enter a new temperature value.'); return; }
    if (!updateReason.trim()) { setError('Please provide a reason for the update.'); return; }

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();
    const newTempInCelsius = convertToCelsius(newTemp, newTempUnit);

    addEditRequest({
      id: `ER${Date.now()}`,
      patientId: selectedPatient.id,
      patientName: selectedPatient.name,
      vitalId: existingVital.id,
      vitalType: 'temperature',
      currentValue: existingVital.value,
      newValue: newTempInCelsius,
      requestedBy: user.username,
      requestedAt: timestamp,
      reason: updateReason,
      status: 'pending',
    });

    addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'Edit Request',
      details: `Requested Temperature update for ${selectedPatient.name} (${selectedPatient.id}): ${existingVital.value}°C → ${newTempInCelsius}°C`,
      ipAddress, deviceName: device, browser, timestamp,
    });

    setShowUpdateModal(false);
    setNewTemp(''); setNewTempUnit('celsius'); setUpdateReason('');
    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Temperature Entry</h2>
          <p className="text-sm text-gray-500 mt-1">Search for a patient and record temperature reading</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {success && (
            <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-500 text-base"></i>
              Temperature recorded successfully!
            </div>
          )}
          {updateSuccess && (
            <div className="mb-5 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-send-plane-line text-teal-500 text-base"></i>
              Update request submitted. Awaiting admin approval.
            </div>
          )}
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 text-base"></i>
              {error}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Patient <span className="text-red-500">*</span></label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400"></i>
              </div>
              <input type="text" value={searchTerm} onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by ID, Name, or Contact Number"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm" />
            </div>
            {showResults && searchResults.length > 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto z-10 relative">
                {searchResults.map((patient) => (
                  <button key={patient.id} onClick={() => selectPatient(patient)}
                    className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left cursor-pointer">
                    <p className="text-sm font-medium text-gray-800">{patient.name}</p>
                    <p className="text-xs text-gray-500">{patient.id} • {patient.contactNumber}</p>
                  </button>
                ))}
              </div>
            )}
            {showResults && searchResults.length === 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                <p className="text-sm text-gray-500">No patients found</p>
              </div>
            )}
          </div>

          {selectedPatient && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-orange-800 mb-2">Selected Patient</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-600">Name:</span><span className="ml-2 font-medium text-gray-800">{selectedPatient.name}</span></div>
                <div><span className="text-gray-600">ID:</span><span className="ml-2 font-medium text-gray-800">{selectedPatient.id}</span></div>
                <div><span className="text-gray-600">Age:</span><span className="ml-2 font-medium text-gray-800">{selectedPatient.age} years</span></div>
                <div><span className="text-gray-600">Gender:</span><span className="ml-2 font-medium text-gray-800">{selectedPatient.gender}</span></div>
                <div className="col-span-2 mt-1">
                  <span className="text-gray-600">Payment:</span>{' '}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ml-1 ${
                    selectedPatient.paymentStatus === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    <i className={selectedPatient.paymentStatus === 'paid' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'}></i>
                    {selectedPatient.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {alreadyRecorded && existingVital && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-alert-line text-orange-500"></i>
                    <span className="text-sm font-semibold text-orange-800">Temperature Already Recorded</span>
                  </div>
                  <p className="text-sm text-orange-700">Current value: <span className="font-bold">{existingVital.value}°C</span></p>
                  <p className="text-xs text-orange-600 mt-0.5">Recorded by {existingVital.recordedBy} on {new Date(existingVital.recordedAt).toLocaleString()}</p>
                </div>
                <button onClick={() => setShowUpdateModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-edit-line"></i>
                  Request Update
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Temperature <span className="text-red-500">*</span></label>
              <input type="number" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)}
                placeholder={unit === 'celsius' ? '37.0' : '98.6'}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                disabled={alreadyRecorded} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setUnit('celsius')} disabled={alreadyRecorded}
                  className={`flex-1 px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${unit === 'celsius' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                  Celsius (°C)
                </button>
                <button type="button" onClick={() => setUnit('fahrenheit')} disabled={alreadyRecorded}
                  className={`flex-1 px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${unit === 'fahrenheit' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                  Fahrenheit (°F)
                </button>
              </div>
            </div>
            {!alreadyRecorded && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                <span className="text-sm text-gray-600">Live Reading Preview:</span>
                <span className="text-2xl font-bold text-orange-600">{getPreviewValue()}</span>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={loading || !selectedPatient || !temperature || alreadyRecorded}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm">
                {loading ? 'Recording...' : 'Record Temperature'}
              </button>
              <button type="button" onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {showUpdateModal && existingVital && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Request Temperature Update</h3>
              <button onClick={() => setShowUpdateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="mb-4 bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Current value:</span>
              <span className="ml-2 font-bold text-gray-800">{existingVital.value}°C</span>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">New Temperature <span className="text-red-500">*</span></label>
              <input type="number" step="0.1" value={newTemp} onChange={(e) => setNewTemp(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                placeholder="e.g. 37.2" />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Unit</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setNewTempUnit('celsius')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium cursor-pointer whitespace-nowrap ${newTempUnit === 'celsius' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>
                  Celsius (°C)
                </button>
                <button type="button" onClick={() => setNewTempUnit('fahrenheit')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium cursor-pointer whitespace-nowrap ${newTempUnit === 'fahrenheit' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>
                  Fahrenheit (°F)
                </button>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Reason for Update <span className="text-red-500">*</span></label>
              <textarea value={updateReason} onChange={(e) => setUpdateReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm resize-none"
                rows={3} placeholder="Explain why this value needs to be updated..." maxLength={300} />
            </div>
            <div className="flex gap-3">
              <button onClick={handleUpdateRequest}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm">
                Submit Request
              </button>
              <button onClick={() => setShowUpdateModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
